/**
 * The default-cursor click resolver (REQ-UI-105) — what a click *means* when
 * no draw tool is active.
 *
 * Sibling of :file:`./annotation-edit-hit`, and separate from the pointer
 * plumbing for the same reason: the precedence is pinned by tests rather than
 * emerging from the order MapLibre happens to invoke handlers in. That
 * ordering is real here — the annotation select handler is registered map-wide
 * (``map.on("click", fn)``) while every entity glyph registers layer-scoped
 * (``map.on("click", layerId, fn)``), so both run on the same click and which
 * runs first is mount-order luck.
 *
 * Priority, most specific first::
 *
 *     entity layer hit  →  defer (the glyph's own handler owns this click)
 *     annotation hit    →  select that annotation
 *     nothing           →  clear the annotation selection
 *
 * # Why an entity beats an annotation it sits inside
 *
 * Annotation fills are hit-testable, so ``queryRenderedFeatures`` reports the
 * fill for any click *within* a circle or polygon — including a click on a
 * detection glyph drawn inside an operator's AOI, which is the common case
 * (drawing an area around a cluster and then inspecting what is in it). Before
 * this resolver such a click re-selected the shape, and because a selected
 * annotation takes unconditional precedence in Entity Details
 * (:file:`@/entity-details/entity-details-panel`) the panel stayed pinned to
 * the shape — the operator could not reach the detection without first
 * deselecting the shape on the map. Architect direction 2026-08-31: the entity
 * wins.
 *
 * Deferring rather than clearing is deliberate. The glyph's layer-scoped
 * handler writes the selection bus, and the bus clears the annotation as it
 * goes (``@/selection/selection-store``), so the clear happens exactly once, at
 * the point the replacement selection is made. Clearing here as well would be a
 * redundant write on the same gesture.
 *
 * Selecting a shape by its **interior** is unaffected — only a click that
 * actually lands on an entity layer defers.
 *
 * Cross-references:
 *   - REQ-UI-105 (annotation select → Entity Details; mutual exclusion).
 *   - ADR-034 D7 (editing surface) / ADR-024 §5 (one focus at a time).
 *   - @/map/background-deselect (``ENTITY_SELECTION_LAYERS`` — the same
 *     hit-test, and the same handler-order hazard, on the deselect path).
 *   - @/map/use-annotation-draw (the hook that applies the resolved action).
 */

/** What a default-cursor map click should do to the annotation selection. */
export type AnnotationClickAction =
  /** An entity glyph owns this click; leave the annotation selection alone. */
  | { readonly type: "defer" }
  /** Select this annotation (a shape was hit, no entity was). */
  | { readonly type: "select"; readonly annotationId: string }
  /** Empty map — drop any annotation selection. */
  | { readonly type: "clear" };

/** What lay under the cursor, read off the rendered features. */
export interface AnnotationClickHit {
  /**
   * Whether the click landed on any layer carrying a clickable entity —
   * platform markers + labels, cluster bubbles + counts, detection and alert
   * glyphs. Computed by ``!isBackgroundClick(map, point)`` against
   * ``ENTITY_SELECTION_LAYERS``, so the two paths share one definition of
   * "an entity is under the cursor".
   */
  readonly hitsEntityLayer: boolean;
  /** The ``annotation_id`` of the topmost annotation feature, or ``null``. */
  readonly annotationIdUnderCursor: string | null;
}

/**
 * Resolve a default-cursor map click against what lies under it.
 *
 * Pure: the caller supplies the hit-test results and applies the returned
 * action. Callers must only invoke this with no draw tool active — with a tool
 * active the click is a draw gesture and never reaches selection.
 */
export function resolveAnnotationClick(hit: AnnotationClickHit): AnnotationClickAction {
  if (hit.hitsEntityLayer) {
    return { type: "defer" };
  }
  if (hit.annotationIdUnderCursor !== null) {
    return { type: "select", annotationId: hit.annotationIdUnderCursor };
  }
  return { type: "clear" };
}
